/**
 * <hauser-consent-banner> — Custom Element ESM (ha/358 Phase F).
 *
 * Bandeau RGPD discrete (bas du viewport) qui s'affiche tant que l'utilisateur
 * connecté n'a pas accordé son consent pour les `kinds` demandés. Anonyme :
 * affiche le banner mais le serveur ne persiste pas (success=false silencieux).
 *
 * Attributes :
 *   data-kinds     — CSV des kinds demandés (défaut "cookies")
 *   data-version   — version du document (défaut "v1")
 *
 * Comportement :
 *   1. Au mount, fetch /ha/form `hauser.consent.status` avec kinds
 *   2. Si TOUS les kinds ont `granted=true` à la même version → no-op
 *   3. Sinon → render banner avec "Accepter" + "Personnaliser"
 *   4. "Accepter" → record(kind=cookies, granted=true) + hide + localStorage
 *   5. "Personnaliser" → navigue vers /cookie-consent
 *
 * localStorage : `hauser-consent-last-check=<timestamp>` pour éviter re-fetch
 * dans la même session.
 */

import { fetchConsentStatus, recordConsent } from "../api.js";

const STORAGE_KEY = "hauser-consent-last-check";
const STORAGE_FRESH_MS = 60 * 60 * 1000; // 1h

export class HauserConsentBanner extends HTMLElement {
    private _dismissed = false;

    connectedCallback(): void {
        this.hidden = true;
        if (this._isFresh()) return;
        void this._maybeShow();
    }

    /** Test seam : force le banner visible / caché. */
    show(): void {
        this.hidden = false;
        this._render();
    }

    /** Test seam : marque comme dismissed. */
    dismiss(): void {
        this._dismissed = true;
        this.hidden = true;
        this.replaceChildren();
    }

    private async _maybeShow(): Promise<void> {
        const kinds = this._readKinds();
        const version = this._readVersion();
        const status = await fetchConsentStatus(kinds);
        if (this._dismissed || !this.isConnected) return;
        const allGranted = kinds.every(k => {
            const entry = status[k];
            return entry !== undefined && entry !== null && entry.granted === true && entry.version === version;
        });
        if (allGranted) {
            this._markFresh();
            return;
        }
        this.show();
    }

    private _render(): void {
        const root = document.createElement("div");
        root.className = "hauser-consent-banner";
        root.setAttribute("role", "dialog");
        root.setAttribute("aria-label", "Consentement cookies");

        const text = document.createElement("p");
        text.className = "hauser-consent-banner__text";
        text.textContent = "Nous utilisons des cookies pour améliorer votre expérience. Vous pouvez accepter ou personnaliser vos préférences.";

        const actions = document.createElement("div");
        actions.className = "hauser-consent-banner__actions";

        const accept = document.createElement("button");
        accept.type = "button";
        accept.className = "hauser-consent-banner__accept";
        accept.textContent = "Accepter";
        accept.addEventListener("click", () => { void this._handleAccept(); });

        const customize = document.createElement("a");
        customize.className = "hauser-consent-banner__customize";
        customize.href = "/cookie-consent";
        customize.textContent = "Personnaliser";

        actions.append(accept, customize);
        root.append(text, actions);
        this.replaceChildren(root);
    }

    private async _handleAccept(): Promise<void> {
        const version = this._readVersion();
        const kinds = this._readKinds();
        const acceptBtn = this.querySelector<HTMLButtonElement>(".hauser-consent-banner__accept");
        if (acceptBtn !== null) acceptBtn.disabled = true;

        let anyOk = false;
        for (const kind of kinds) {
            const ok = await recordConsent(kind, version, true);
            if (ok) anyOk = true;
        }
        if (anyOk) {
            this._markFresh();
            this.dismiss();
            this.dispatchEvent(new CustomEvent("hauser:consent:accepted", {
                bubbles: true,
                detail: { kinds, version },
            }));
        } else {
            // Anonyme : pas d'enregistrement DB, on hide quand même pour ne pas spammer.
            this.dismiss();
        }
    }

    private _readKinds(): string[] {
        const raw = (this.getAttribute("data-kinds") ?? "cookies").trim();
        const out = raw.split(",").map(s => s.trim()).filter(s => s !== "");
        return out.length > 0 ? out : ["cookies"];
    }

    private _readVersion(): string {
        const v = this.getAttribute("data-version");
        return v !== null && v !== "" ? v : "v1";
    }

    private _isFresh(): boolean {
        if (typeof localStorage === "undefined") return false;
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw === null) return false;
            const ts = parseInt(raw, 10);
            if (!Number.isFinite(ts)) return false;
            return (Date.now() - ts) < STORAGE_FRESH_MS;
        } catch {
            return false;
        }
    }

    private _markFresh(): void {
        if (typeof localStorage === "undefined") return;
        try {
            localStorage.setItem(STORAGE_KEY, String(Date.now()));
        } catch {
            /* quota / private mode — silent */
        }
    }
}

if (typeof customElements !== "undefined" && !customElements.get("hauser-consent-banner")) {
    customElements.define("hauser-consent-banner", HauserConsentBanner);
}
